import React, { useState, useCallback } from 'react';

const fetchProductDataFromNetSuite = async (upc, authToken) => {
  console.log('Fetching product data for UPC:', upc);

  try {
    const requestBody = {
      upc,
      useInternalAuth: true
    };

    // If authToken is provided, use it instead of internal auth
    if (authToken) {
      requestBody.useInternalAuth = false;
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if authToken is provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${apiBase}/mixo`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch data from NetSuite');
    }

    const data = await response.json();
    console.log('NetSuite response:', data);
    
    if (data.items && data.items.length > 0) {
      // Filter to only retail pricing options
      const retailItems = data.items.filter(item => 
        item.pricelevelname && item.pricelevelname.toLowerCase().includes('retail')
      );
      
      // Extract future prices from any item that has them (they're item-level, not per pricing row)
      const futurePrice = data.items.find(item => item.custitem_bmc_future_price)?.custitem_bmc_future_price || null;
      const cadFuturePrice = data.items.find(item => item.custitem_bmc_cad_future_price)?.custitem_bmc_cad_future_price || null;

      // Extract outlet prices (from dedicated filtered joins in the API query)
      const usdOutletPrice = data.items.find(item => item.usd_outlet_price)?.usd_outlet_price || null;
      const cadOutletPrice = data.items.find(item => item.cad_outlet_price)?.cad_outlet_price || null;

      console.log('o0o0o', JSON.stringify(data));
      return {
        success: true,
        data: {
          upc: data.items[0].upccode || upc,
          name: data.items[0].displayname,
          sku: data.items[0].itemid,
          id: data.items[0].id,
          futurePrice,
          cadFuturePrice,
          usdOutletPrice,
          cadOutletPrice,
          pricingOptions: retailItems.map(item => ({
            currency: item.currency,
            pricelevelname: item.pricelevelname,
            unitprice: item.unitprice,
            pattern: item.pattern, 
            config: item.config,
            itemData: item // Keep full item data for reference
          }))
        }
      };
    } else {
      return { success: false, error: 'Product not found' };
    }
  } catch (error) {
    console.error('Error fetching from NetSuite:', error);
    return { success: false, error: error.message || 'Failed to fetch product data' };
  }
};

const UpcSelect = ({ onProductDataFetched, netSuiteToken }) => {
  const [upcInput, setUpcInput] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingProduct, setPendingProduct] = useState(null); // Product awaiting selection
  const [selectedRegion, setSelectedRegion] = useState('US'); // 'US' or 'CA'
  const [selectedPriceType, setSelectedPriceType] = useState('current'); // 'current' or 'future'

  const handleUpcChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    setUpcInput(numericValue);
    setError('');
  };

  const handleFetchData = useCallback(async () => {
    if (!upcInput) {
      setError('Please enter a UPC.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setPendingProduct(null);
    setCurrentProduct(null);
    // Clear any existing product data to force re-render
    onProductDataFetched(null);

    const result = await fetchProductDataFromNetSuite(upcInput, netSuiteToken);

    setIsLoading(false);
    
    if (result.success) {
      // Always show selection UI for region and price type
      setPendingProduct(result.data);
    } else {
      setError(result.error || 'Failed to fetch product data.');
    }
  }, [upcInput, netSuiteToken, onProductDataFetched]);

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
  };

  const handlePriceTypeChange = (priceType) => {
    setSelectedPriceType(priceType);
  };

  const getSelectedPrice = () => {
    if (!pendingProduct) return null;
    
    // If no price is selected (Filson only), return empty string
    if (selectedPriceType === 'none') {
      return '';
    }
    
    // Future prices are stored at the product level, not per pricing row
    if (selectedPriceType === 'future') {
      const price = selectedRegion === 'CA' ? pendingProduct.cadFuturePrice : pendingProduct.futurePrice;
      return price || null;
    }

    // Outlet prices from dedicated filtered joins
    if (selectedPriceType === 'outlet') {
      const price = selectedRegion === 'CA' ? pendingProduct.cadOutletPrice : pendingProduct.usdOutletPrice;
      return price || null;
    }

    // For current price, we need a retail pricing option for the selected region
    if (!pendingProduct.pricingOptions.length) return null;
    
    const currency = selectedRegion === 'CA' ? 'CAD' : 'USD';
    const retailOption = pendingProduct.pricingOptions.find(option => 
      option.currency === currency
    );
    
    return retailOption ? retailOption.unitprice : null;
  };

  const getSelectedCurrency = () => {
    // If no price is selected, return empty string
    if (selectedPriceType === 'none') {
      return '';
    }
    return selectedRegion === 'CA' ? 'CAD' : 'USD';
  };

  const handleConfirmSelection = () => {
    if (pendingProduct) {
      const selectedPrice = getSelectedPrice();
      const currency = getSelectedCurrency();
      console.log(pendingProduct, 'pendingProduct')
      
      // Get the first available itemData to extract custom fields
      const firstItemData = pendingProduct.pricingOptions?.[0]?.itemData || {};
      
      const finalProductData = {
        ...pendingProduct,
        region: selectedRegion,
        priceType: selectedPriceType,
        currency: currency,
        retailPrice: selectedPrice,
        finalPrice: selectedPrice,
        // Add custom NetSuite fields from itemData
        custitem_psgss_style_number: firstItemData.custitem_psgss_style_number,
        pattern: firstItemData.pattern,
        configuration: firstItemData.configuration,
        product_color: firstItemData.product_color,
        product_size: firstItemData.product_size
      };
      
      // Add Shinola-specific fields only if they exist
      if (firstItemData.brand) {
        finalProductData.brand = firstItemData.brand;
      }
      if (firstItemData.custitem_psgss_nrf_color_code) {
        finalProductData.custitem_psgss_nrf_color_code = firstItemData.custitem_psgss_nrf_color_code;
      }
      if (firstItemData.custitem_psgss_nrf_size_code) {
        finalProductData.custitem_psgss_nrf_size_code = firstItemData.custitem_psgss_nrf_size_code;
      }
      if (firstItemData.battery_expiration_date) {
        finalProductData.battery_expiration_date = firstItemData.battery_expiration_date;
      }
      
      setCurrentProduct(finalProductData);
      onProductDataFetched(finalProductData);
      
      // Reset selection state
      setPendingProduct(null);
    }
  };

  const handleCancelSelection = () => {
    setPendingProduct(null);
  };

  const handleClearProduct = () => {
    setCurrentProduct(null);
    setUpcInput('');
    onProductDataFetched(null);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      handleFetchData();
    }
  };

  return (
    <div className="upc-select-container">
      <style jsx>{`
        .upc-select-container {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .product-details-container {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
          margin-top: 20px;
        }
        
        .product-details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .product-title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin: 0;
        }
        
        .clear-btn {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }
        
        .clear-btn:hover {
          background-color: #c82333;
        }
        
        .product-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }
        
        .info-item {
          display: flex;
          flex-direction: column;
        }
        
        .info-label {
          font-weight: 600;
          color: #666;
          font-size: 14px;
          margin-bottom: 4px;
        }
        
        .info-value {
          font-size: 16px;
          color: #333;
        }
        
        .price-highlight {
          background-color: #e3f2fd;
          padding: 10px;
          border-radius: 4px;
          border-left: 4px solid #667eea;
        }

        .selection-modal {
          background-color: #fff;
          border: 2px solid #667eea;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .selection-section {
          margin: 15px 0;
        }

        .selection-section h5 {
          margin-bottom: 10px;
          color: #333;
          font-weight: 600;
        }

        .radio-group {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 140px;
          position: relative;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .radio-option:hover {
          border-color: #667eea;
          background-color: #e3f2fd;
          transform: translateY(-1px);
        }

        .radio-option:active {
          transform: translateY(0);
        }

        .radio-option input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          cursor: pointer;
          z-index: 1;
        }

        .radio-option input[type="radio"]:checked + .radio-label {
          color: white;
        }

        .radio-option input[type="radio"]:checked {
          background: #667eea;
          border-color: #667eea;
        }

        .radio-option:has(input[type="radio"]:checked) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
        }

        .radio-option input[type="radio"]:focus {
          outline: none;
        }

        .radio-option:has(input[type="radio"]:focus) {
          outline: 2px solid #667eea;
          outline-offset: 2px;
        }

        .radio-label {
          margin: 0;
          cursor: pointer;
          font-weight: 500;
          font-size: 1rem;
          pointer-events: none;
          position: relative;
          z-index: 0;
        }

        .price-preview {
          margin-top: 15px;
          padding: 15px;
          background-color: #e3f2fd;
          border-radius: 6px;
          border-left: 4px solid #667eea;
        }

        .modal-actions {
          margin-top: 20px;
          display: flex;
          gap: 10px;
        }

        .btn-confirm {
          background-color: #28a745;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .btn-confirm:hover {
          background-color: #218838;
        }

        .btn-confirm:disabled {
          background-color: #bdbdbd !important;
          color: #f5f5f5 !important;
          cursor: not-allowed !important;
          border: none;
        }

        .btn-cancel {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
        }

        .btn-cancel:hover {
          background-color: #5a6268;
        }

        .input-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .form-control {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #dee2e6;
          border-radius: 6px;
          font-size: 16px;
          min-height: 44px;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
          box-sizing: border-box;
        }

        .form-control:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn {
          padding: 12px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 16px;
          min-height: 44px;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .btn:active {
          transform: scale(0.98);
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .btn-primary:hover:not(:disabled) {
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        .text-danger {
          color: #dc3545;
          font-size: 14px;
          margin-top: 8px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 600;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .input-group {
            flex-direction: column;
            gap: 12px;
          }

          .form-control {
            width: 100%;
            padding: 16px;
            font-size: 16px;
          }

          .btn {
            width: 100%;
            padding: 16px;
            font-size: 1.1rem;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }

          .radio-option {
            min-width: auto;
            padding: 16px 20px;
            font-size: 1rem;
            min-height: 48px;
          }

          .radio-option input[type="radio"] {
            width: 100%;
            height: 100%;
            min-height: 48px;
          }

          .radio-label {
            font-size: 1.1rem;
          }

          .modal-actions {
            flex-direction: column-reverse;
            gap: 12px;
          }

          .btn-confirm,
          .btn-cancel {
            width: 100%;
            padding: 16px;
            font-size: 1.1rem;
          }

          .product-info-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .clear-btn {
            padding: 12px 16px;
            font-size: 1rem;
            min-height: 44px;
          }
        }

        /* Safari-specific fixes */
        @supports (-webkit-touch-callout: none) {
          .form-control,
          .btn,
          .radio-option {
            -webkit-appearance: none;
            -webkit-border-radius: 6px;
          }
        }

        /* iOS Safari zoom prevention */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          input[type="text"] {
            font-size: 16px !important;
          }
        }
      `}</style>
      
      <div className="form-group">
        <label htmlFor="upc-input"><strong>UPC</strong></label>
        <div className="input-group">
          <input
            type="text"
            id="upc-input"
            value={upcInput}
            onChange={handleUpcChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter or scan UPC (numbers only)"
            className="form-control"
            disabled={isLoading}
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <button
            type="button"
            onClick={handleFetchData}
            className="btn btn-primary"
            disabled={isLoading || !upcInput}
          >
            {isLoading ? 'Loading...' : 'Fetch Item'}
          </button>
        </div>
        {error && <p className="text-danger mt-2">{error}</p>}
      </div>

      {/* Selection Modal */}
      {pendingProduct && (
        <div className="selection-modal">
          <h4>Configure Pricing for: {pendingProduct.name}</h4>
          <p><strong>UPC:</strong> {pendingProduct.upc} | <strong>SKU:</strong> {pendingProduct.sku}</p>
          
          <div className="selection-section">
            <h5>Select Region:{selectedPriceType === 'none' && <span style={{ fontSize: '0.85rem', color: '#999', fontWeight: 'normal', marginLeft: '8px' }}>(N/A for no price)</span>}</h5>
            <div className="radio-group">
              <div className="radio-option" style={{ opacity: selectedPriceType === 'none' ? 0.5 : 1 }}>
                <input
                  type="radio"
                  id="region-us"
                  name="region"
                  value="US"
                  checked={selectedRegion === 'US'}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  disabled={selectedPriceType === 'none'}
                />
                <span className="radio-label">United States (USD)</span>
              </div>
              <div className="radio-option" style={{ opacity: selectedPriceType === 'none' ? 0.5 : 1 }}>
                <input
                  type="radio"
                  id="region-ca"
                  name="region"
                  value="CA"
                  checked={selectedRegion === 'CA'}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  disabled={selectedPriceType === 'none'}
                />
                <span className="radio-label">Canada (CAD)</span>
              </div>
            </div>
          </div>

          <div className="selection-section">
            <h5>Select Price Type:</h5>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="price-current"
                  name="priceType"
                  value="current"
                  checked={selectedPriceType === 'current'}
                  onChange={(e) => handlePriceTypeChange(e.target.value)}
                />
                <span className="radio-label">Current Retail Price</span>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="price-future"
                  name="priceType"
                  value="future"
                  checked={selectedPriceType === 'future'}
                  onChange={(e) => handlePriceTypeChange(e.target.value)}
                />
                <span className="radio-label">Future Retail Price</span>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="price-outlet"
                  name="priceType"
                  value="outlet"
                  checked={selectedPriceType === 'outlet'}
                  onChange={(e) => handlePriceTypeChange(e.target.value)}
                />
                <span className="radio-label">Outlet Price</span>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="price-none"
                  name="priceType"
                  value="none"
                  checked={selectedPriceType === 'none'}
                  onChange={(e) => handlePriceTypeChange(e.target.value)}
                />
                <span className="radio-label">🚫 No Price</span>
              </div>
            </div>
          </div>

          {(getSelectedPrice() !== null || selectedPriceType === 'none') && (
            <div className="price-preview">
              <strong>Selected Configuration:</strong><br/>
              {selectedPriceType === 'none' ? (
                <>
                  Price Type: No Price<br/>
                  <strong>Price: No price will be shown on label</strong>
                </>
              ) : (
                <>
                  Region: {selectedRegion} ({getSelectedCurrency()})<br/>
                  Price Type: {selectedPriceType === 'current' ? 'Current Retail' : selectedPriceType === 'outlet' ? 'Outlet' : 'Future Retail'}<br/>
                  <strong>Price: ${getSelectedPrice()}</strong>
                </>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button 
              className="btn-confirm" 
              onClick={handleConfirmSelection}
              disabled={selectedPriceType !== 'none' && !getSelectedPrice()}
            >
              Confirm Selection
            </button>
            <button className="btn-cancel" onClick={handleCancelSelection}>
              Cancel
            </button>
          </div>
          {selectedPriceType !== 'none' && !getSelectedPrice() && (
            <div style={{ color: '#888', marginTop: 8, fontSize: 14, textAlign: 'center' }}>
              Please select a region and price type with available pricing to enable confirmation.
            </div>
          )}
        </div>
      )}
      
      {/* Product Details Display */}
      {currentProduct && (
        <div className="product-details-container">
          <div className="product-details-header">
            <h4 className="product-title">{currentProduct.name}</h4>
            <button className="clear-btn" onClick={handleClearProduct}>
              Clear
            </button>
          </div>
          
          <div className="product-info-grid">
            <div className="info-item">
              <span className="info-label">UPC:</span>
              <span className="info-value">{currentProduct.upc}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">SKU:</span>
              <span className="info-value">{currentProduct.sku}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Item ID:</span>
              <span className="info-value">{currentProduct.id}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Display Name:</span>
              <span className="info-value">{currentProduct.name}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Region:</span>
              <span className="info-value">
                {currentProduct.priceType === 'none' ? (
                  <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>N/A</span>
                ) : (
                  `${currentProduct.region} (${currentProduct.currency})`
                )}
              </span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Price Type:</span>
              <span className="info-value">
                {currentProduct.priceType === 'none' ? 'No Price' : currentProduct.priceType === 'current' ? 'Current' : 'Future'} 
                {currentProduct.priceType !== 'none' && ' Retail'}
              </span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Style Number:</span>
              <span className="info-value">{currentProduct.custitem_psgss_style_number || 'N/A'}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Pattern:</span>
              <span className="info-value">{currentProduct.pattern || 'N/A'}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Configuration:</span>
              <span className="info-value">{currentProduct.configuration || 'N/A'}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Color:</span>
              <span className="info-value">{currentProduct.product_color || 'N/A'}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Size:</span>
              <span className="info-value">{currentProduct.product_size || 'N/A'}</span>
            </div>
            
            {currentProduct.brand && (
              <div className="info-item">
                <span className="info-label">Brand:</span>
                <span className="info-value">{currentProduct.brand}</span>
              </div>
            )}
            
            {currentProduct.custitem_psgss_nrf_color_code && (
              <div className="info-item">
                <span className="info-label">NRF Color Code:</span>
                <span className="info-value">{currentProduct.custitem_psgss_nrf_color_code}</span>
              </div>
            )}
            
            {currentProduct.custitem_psgss_nrf_size_code && (
              <div className="info-item">
                <span className="info-label">NRF Size Code:</span>
                <span className="info-value">{currentProduct.custitem_psgss_nrf_size_code}</span>
              </div>
            )}
            
            {currentProduct.battery_expiration_date && (
              <div className="info-item">
                <span className="info-label">Battery Expiration:</span>
                <span className="info-value">{currentProduct.battery_expiration_date}</span>
              </div>
            )}
            
            <div className="info-item price-highlight">
              <span className="info-label">Retail Price:</span>
              <span className="info-value" style={{fontSize: '18px', fontWeight: '600'}}>
                {currentProduct.priceType === 'none' ? (
                  <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>No price</span>
                ) : (
                  `$${currentProduct.retailPrice}`
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcSelect;